import type { ISelectedUser } from '@/apiClient/types/user'
import type {
  IWebsitePermissionRequest,
  IWebsiteUpdateBody,
} from '@/apiClient/types/websites'

import type { WebsiteVisibilityMode } from './types'

function getUserIdentity(user: ISelectedUser): string {
  return user.id || user.employee_id || user.email || user.name || ''
}

export function mergeShareUsersForPrivate(
  usersWithAccess: ISelectedUser[],
  shareUsers: ISelectedUser[],
): ISelectedUser[] {
  const merged = new Map<string, ISelectedUser>()

  for (const user of [...usersWithAccess, ...shareUsers]) {
    const identity = getUserIdentity(user)
    if (!identity) continue

    const existing = merged.get(identity)
    if (!existing) {
      merged.set(identity, user)
      continue
    }

    if (!existing.employee_id && user.employee_id) {
      merged.set(identity, user)
    }
  }

  return [...merged.values()]
}

export function buildWebsiteVisibilityBody(
  mode: WebsiteVisibilityMode,
  password: string,
  shareUsers: ISelectedUser[],
  options?: { hadPassword?: boolean; usersWithAccess?: ISelectedUser[] },
): Pick<
  IWebsiteUpdateBody,
  'is_public' | 'password' | 'remove_password' | 'permissions'
> {
  const permissions = toWebsitePermissionRequests(
    mergeShareUsersForPrivate(options?.usersWithAccess ?? [], shareUsers),
  )

  switch (mode) {
    case 'foundever':
      return {
        is_public: true,
        ...(options?.hadPassword ? { remove_password: true } : {}),
        permissions: [],
      }
    case 'private':
      return {
        is_public: false,
        ...(options?.hadPassword ? { remove_password: true } : {}),
        permissions,
      }
    case 'password': {
      const trimmed = password.trim()
      return {
        is_public: true,
        ...(trimmed ? { password: trimmed } : {}),
        permissions: [],
      }
    }
  }
}

export function toWebsitePermissionRequests(
  users: ISelectedUser[],
): IWebsitePermissionRequest[] {
  // The backend accepts either identifier and needs at least one. We send a
  // single identifier per entry, preferring `user_id`: it is applied directly,
  // so it both keeps grantees whose permission row came back without an
  // `employee_id` (soft-deleted users, or users whose `employee_id` is the
  // empty-string default) — previously dropped here and silently revoked on a
  // full-overwrite save — and skips the warehouse resolver entirely when every
  // entry has one. Users picked from search have no `user_id` until they exist
  // locally (warehouse hits return `id: null`), so those fall back to
  // `employee_id`, exactly as before. An entry with neither identifier would be
  // rejected and fail the whole save, so it is dropped.
  const requests: IWebsitePermissionRequest[] = []

  for (const user of users) {
    if (user.id) {
      requests.push({ user_id: user.id, permission: 'read' })
    } else if (user.employee_id) {
      requests.push({ employee_id: user.employee_id, permission: 'read' })
    }
  }

  return requests
}

export function resolveWebsiteVisibilityMode(input: {
  is_public: boolean
  has_password: boolean
}): WebsiteVisibilityMode {
  if (input.has_password) return 'password'
  if (!input.is_public) return 'private'
  return 'foundever'
}
